import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Header from "../Components/Header/Header";
import Footer from "../Components/Footer/Footer";
import { motion, AnimatePresence } from "framer-motion";
import { 
  User, 
  Award, 
  Users, 
  Plus, 
  Trash2, 
  Check, 
  Loader2, 
  Heart, 
  Calendar, 
  ChevronDown, 
  ChevronUp, 
  Sparkles,
  Info,
  Compass,
  ArrowLeft
} from "lucide-react";

// Replace this with your deployed Google Web App URL (from appscript.js deployment)
const GOOGLE_SHEET_WEB_APP_URL = "https://script.google.com/macros/s/AKfycbwaYWH_9JRNJ7tA1wzBxpt0bXnBRzRN_GrEnp68fOnXrXKuzeY0S8LcXBFs3Y96ZGoV-w/exec";

const RathyatraRegister = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState({
    fullName: "",
    mobileNumber: "",
    address: "",
    gotram: "",
  });

  const [familyMembers, setFamilyMembers] = useState([]);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  // Handle main participant changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "mobileNumber" && !/^\d*$/.test(value)) return;
    if (name === "mobileNumber" && value.length > 10) return;
    setForm({ ...form, [name]: value });
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Add a family member slot
  const handleAddFamilyMember = () => {
    if (familyMembers.length >= 5) return;
    setFamilyMembers([
      ...familyMembers,
      {
        id: Date.now() + Math.random(),
        fullName: "",
        mobileNumber: "",
        relation: "",
        isOpen: true,
      },
    ]);
  };

  // Remove a family member slot
  const handleRemoveFamilyMember = (id) => {
    setFamilyMembers(familyMembers.filter((m) => m.id !== id));
  };

  // Handle family member input changes
  const handleFamilyMemberChange = (id, field, value) => {
    if (field === "mobileNumber" && !/^\d*$/.test(value)) return;
    if (field === "mobileNumber" && value.length > 10) return;
    setFamilyMembers(
      familyMembers.map((m) =>
        m.id === id ? { ...m, [field]: value } : m
      )
    );
  };

  // Toggle family member accordion open/closed
  const toggleFamilyMemberAccordion = (id) => {
    setFamilyMembers(
      familyMembers.map((m) =>
        m.id === id ? { ...m, isOpen: !m.isOpen } : m
      )
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    const newErrors = {};

    // Validate main details
    if (!form.fullName.trim()) newErrors.fullName = "Full name is required.";
    if (!form.mobileNumber) newErrors.mobileNumber = "Mobile number is required.";
    else if (form.mobileNumber.length !== 10)
      newErrors.mobileNumber = "Enter a valid 10-digit mobile number.";
    if (!form.address.trim()) newErrors.address = "Address is required.";

    // Validate family details
    familyMembers.forEach((member, index) => {
      if (!member.fullName.trim()) {
        newErrors[`member_${member.id}_name`] = "Name is required.";
      }
      if (!member.relation) {
        newErrors[`member_${member.id}_relation`] = "Relation is required.";
      }
      if (member.mobileNumber && member.mobileNumber.length !== 10) {
        newErrors[`member_${member.id}_phone`] = "Enter a valid 10-digit number.";
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // Auto-open accordion if there is an error in family members
      const updatedMembers = familyMembers.map((m) => {
        if (newErrors[`member_${m.id}_name`] || newErrors[`member_${m.id}_relation`] || newErrors[`member_${m.id}_phone`]) {
          return { ...m, isOpen: true };
        }
        return m;
      });
      setFamilyMembers(updatedMembers);
      
      // Scroll to error
      const firstError = Object.keys(newErrors)[0];
      const element = document.getElementsByName(firstError)[0] || document.getElementById(firstError);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    try {
      setLoading(true);

      // Construct Google Script Data payload
      const payload = {
        fullName: form.fullName,
        mobileNumber: form.mobileNumber,
        address: form.address,
        gotram: form.gotram || "N/A",
        familyMembersCount: familyMembers.length,
        familyDetails: familyMembers.map(m => `${m.fullName} (${m.relation}${m.mobileNumber ? `, ${m.mobileNumber}` : ""})`).join(" |\n") || "None",
        registrationDate: new Date().toLocaleDateString("en-GB", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      };

      const formBody = new URLSearchParams(payload).toString();

      // Submit data to google script sheet macro
      await fetch(
        GOOGLE_SHEET_WEB_APP_URL,
        {
          method: "POST",
          mode: "no-cors",
          body: formBody,
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
        }
      );

      // Simulate a small delay for premium UX loader feeling
      await new Promise((resolve) => setTimeout(resolve, 800));

      setShowSuccessModal(true);
    } catch (err) {
      console.error(err);
      alert("Something went wrong while submitting. Please check your network and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Reset form after successful modal close
  const handleModalClose = () => {
    setShowSuccessModal(false);
    setForm({
      fullName: "",
      mobileNumber: "",
      address: "",
      gotram: "",
    });
    setFamilyMembers([]);
    setErrors({});
  };

  return (
    <>
      <Header />
    <div className="bg-[#FAF7F0] min-h-screen flex flex-col font-open-sans">

      {/* Hero Banner Section */}
      <div className="relative overflow-hidden bg-gradient-to-br from-darkred via-red to-orange text-white py-16 md:py-24 px-4 sm:px-6 lg:px-8 text-center shadow-2xl">
        {/* Animated backgrounds */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.08)_0%,transparent_60%)] pointer-events-none" />
        
        {/* Rotating Traditional Mandala Graphic */}
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 opacity-[0.06] select-none pointer-events-none w-[600px] h-[600px] animate-[spin_180s_linear_infinite]">
          <svg viewBox="0 0 100 100" className="w-full h-full text-white fill-current">
            <path d="M50 0 A50 50 0 1 0 50 100 A50 50 0 1 0 50 0 M50 10 A40 40 0 1 1 50 90 A40 40 0 1 1 50 10" />
            <circle cx="50" cy="50" r="5" />
            {[...Array(12)].map((_, i) => (
              <path
                key={i}
                d="M50 20 L53 35 L47 35 Z"
                transform={`rotate(${i * 30} 50 50)`}
              />
            ))}
            {[...Array(24)].map((_, i) => (
              <circle
                key={i}
                cx="50"
                cy="15"
                r="1.5"
                transform={`rotate(${i * 15} 50 50)`}
              />
            ))}
          </svg>
        </div>

        {/* Decorative Lotus Bottom Pattern */}
        <div className="absolute bottom-0 left-0 right-0 h-4 bg-gradient-to-r from-orange via-yellow-400 to-orange opacity-40 blur-xs" />

        <div className="max-w-4xl mx-auto relative z-10 space-y-6">
          <Link
            to="/rathyatra"
            className="inline-flex items-center gap-2 text-white transition-colors duration-300  bg-white/10 hover:bg-white/20 px-4 py-2 rounded-full no-underline mr-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to RathYatra Donation
          </Link>

          {/* Top Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-md  border border-white/20 text-xs sm:text-sm  tracking-wide uppercase shadow-inner">
            <Sparkles className="w-4 h-4 text-orange animate-pulse" />
            Rath Yatra Family Blessings Registration
          </div>

          {/* Heading */}
          <h1 className="font-dm-serif text-3xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-tight text-white drop-shadow-md">
            Receive the Divine Blessings of <br className="hidden md:block"/>
            <span className="text-yellow-400">Lord Sri Jagannath</span>
          </h1>

          {/* Subheading */}
          <p className="text-lg sm:text-xl font-bold text-orange-100 max-w-2xl mx-auto tracking-wide">
            Amaravati Sri Jagannath Rath Yatra 2026
          </p>

          {/* Date Badge */}
          <div className="inline-flex items-center gap-2.5 px-6 py-2.5 rounded-2xl bg-amber-500/25 border border-amber-400/40 text-yellow-300 text-sm md:text-base font-bold shadow-md">
            <Calendar className="w-5 h-5" />
            📅 July 16 – July 18, 2026
          </div>

          {/* Description */}
          <p className="text-sm sm:text-base text-gray-200 max-w-2xl mx-auto leading-relaxed">
            On the auspicious occasion of Sri Jagannath Rath Yatra, devotees and well-wishers are invited to register their names and family details to receive the divine blessings of Lord Jagannath, Lord Baladeva, and Devi Subhadra.
          </p>
          <p className="text-xs sm:text-sm text-orange-200/90 font-medium">
            * This registration is open to everyone. Individuals, families, and friends are welcome to participate.
          </p>
        </div>
      </div>

      {/* Main Registration Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 flex-grow w-full">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Left Column: Forms */}
          <div className="lg:col-span-7 space-y-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              
              {/* Form Card: Devotee Details */}
              <div className="bg-white rounded-3xl shadow-xl shadow-amber-900/5 border border-amber-500/10 p-6 sm:p-8 relative overflow-hidden">
                {/* Gold header accent */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red via-orange to-yellow-500" />
                
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2.5 rounded-xl bg-orange/10 text-orange">
                    <User className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-extrabold text-darkred">Your Details</h2>
                    <p className="text-xs text-gray-500">Please enter your main details for the blessing registry.</p>
                  </div>
                </div>

                <div className="space-y-4">
                  {/* Full Name Input */}
                  <div className="relative">
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={form.fullName}
                      onChange={handleInputChange}
                      placeholder=" "
                      className={`peer w-full bg-[#FAF9F6] text-gray-800 placeholder-transparent border rounded-xl px-4 py-3.5 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 ${
                        errors.fullName 
                          ? "border-red focus:border-red" 
                          : "border-gray-200 focus:border-orange"
                      }`}
                    />
                    <label 
                      htmlFor="fullName"
                      className="absolute left-4 -top-2.5 bg-white px-1.5  font-semibold text-orange transition-all duration-300 pointer-events-none 
                      peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3.5 
                      peer-focus:-top-2.5 peer-focus:text-orange"
                    >
                      Full Name *
                    </label>
                    {errors.fullName && (
                      <p className="text-red text-xs mt-1.5 flex items-center gap-1">
                        <Info className="w-3.5 h-3.5" />
                        {errors.fullName}
                      </p>
                    )}
                  </div>

                  {/* Phone & Gotram Group */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    {/* Mobile Number Input */}
                    <div className="relative">
                      <div className="flex items-center">
                        <div className="flex items-center border border-r-0 border-gray-200 bg-gray-50 text-gray-500 rounded-l-xl px-3 py-3.5 text-sm h-[54px]">
                          <span>+91</span>
                        </div>
                        <input
                          type="tel"
                          id="mobileNumber"
                          name="mobileNumber"
                          value={form.mobileNumber}
                          onChange={handleInputChange}
                          placeholder=" "
                          className={`peer w-full bg-[#FAF9F6] text-gray-800 placeholder-transparent border rounded-r-xl px-4 py-3.5 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 h-[54px] ${
                            errors.mobileNumber 
                              ? "border-red focus:border-red" 
                              : "border-gray-200 focus:border-orange"
                          }`}
                        />
                        <label 
                          htmlFor="mobileNumber"
                          className="absolute left-[70px] -top-2.5 bg-white px-1.5 text-xs font-semibold text-orange transition-all duration-300 pointer-events-none 
                          peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3.5 
                          peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-orange"
                        >
                          Mobile Number *
                        </label>
                      </div>
                      {errors.mobileNumber && (
                        <p className="text-red text-xs mt-1.5 flex items-center gap-1">
                          <Info className="w-3.5 h-3.5" />
                          {errors.mobileNumber}
                        </p>
                      )}
                    </div>

                    {/* Gotram Input */}
                    <div className="relative">
                      <input
                        type="text"
                        id="gotram"
                        name="gotram"
                        value={form.gotram}
                        onChange={handleInputChange}
                        placeholder=" "
                        className="peer w-full bg-[#FAF9F6] text-gray-800 placeholder-transparent border border-gray-200 rounded-xl px-4 py-3.5 outline-none transition-all duration-300 focus:border-orange focus:ring-2 focus:ring-orange/20"
                      />
                      <label 
                        htmlFor="gotram"
                        className="absolute left-4 -top-2.5 bg-white px-1.5 text-xs font-semibold text-orange transition-all duration-300 pointer-events-none 
                        peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3.5 
                        peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-orange"
                      >
                        Gotram (Lineage) <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                    </div>
                  </div>

                  {/* Address Input */}
                  <div className="relative">
                    <textarea
                      id="address"
                      name="address"
                      value={form.address}
                      onChange={handleInputChange}
                      placeholder=" "
                      rows={3}
                      className={`peer w-full bg-[#FAF9F6] text-gray-800 placeholder-transparent border rounded-xl px-4 py-3.5 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 resize-none ${
                        errors.address 
                          ? "border-red focus:border-red" 
                          : "border-gray-200 focus:border-orange"
                      }`}
                    />
                    <label 
                      htmlFor="address"
                      className="absolute left-4 -top-2.5 bg-white px-1.5 text-xs font-semibold text-orange transition-all duration-300 pointer-events-none 
                      peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-3.5 
                      peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-orange"
                    >
                      Residential Address *
                    </label>
                    {errors.address && (
                      <p className="text-red text-xs mt-1.5 flex items-center gap-1">
                        <Info className="w-3.5 h-3.5" />
                        {errors.address}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Form Card: Family Details (Accordion-Based) */}
              <div className="bg-white rounded-3xl shadow-xl shadow-amber-900/5 border border-amber-500/10 p-6 sm:p-8 relative overflow-hidden">
                {/* Gold header accent */}
                <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-red via-orange to-yellow-500" />
                
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 rounded-xl bg-orange/10 text-orange">
                      <Users className="w-6 h-6" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-darkred">Family Blessings</h2>
                      <p className="text-sm text-gray-500">You may include up to 5 family members to receive blessings.</p>
                    </div>
                  </div>
                  <span className="text-xs font-bold text-orange bg-orange/10 px-2.5 py-1 rounded-full">
                    {familyMembers.length} / 5 Added
                  </span>
                </div>

                {familyMembers.length === 0 ? (
                  <div className="text-center py-10 border-2 border-dashed border-gray-100 rounded-2xl bg-[#FAF9F6] p-6">
                    <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                    <p className="text-sm font-bold text-gray-700">No family members added yet</p>
                    <p className="text-xs text-gray-400 mt-1 max-w-sm mx-auto">
                      Participate as a family to receive Lord Jagannath's ultimate blessings together.
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <AnimatePresence initial={false}>
                      {familyMembers.map((member, index) => (
                        <motion.div
                          key={member.id}
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.25 }}
                          className="border border-orange/10 rounded-2xl overflow-hidden shadow-sm"
                        >
                          {/* Accordion Trigger Header */}
                          <div 
                            onClick={() => toggleFamilyMemberAccordion(member.id)}
                            className="flex items-center justify-between px-5 py-3.5 bg-orange/5 hover:bg-orange/10 cursor-pointer transition-colors duration-200"
                          >
                            <span className="font-bold text-sm text-darkred flex items-center gap-2">
                              <Heart className="w-4 h-4 text-orange fill-orange/20" />
                              Family Member #{index + 1}
                              {member.fullName && <span className="font-normal text-gray-600">({member.fullName})</span>}
                            </span>
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveFamilyMember(member.id);
                                }}
                                className="p-1.5 text-gray-400 hover:text-red rounded-lg transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                              {member.isOpen ? (
                                <ChevronUp className="w-4 h-4 text-orange" />
                              ) : (
                                <ChevronDown className="w-4 h-4 text-orange" />
                              )}
                            </div>
                          </div>

                          {/* Accordion Content Panel */}
                          <AnimatePresence initial={false}>
                            {member.isOpen && (
                              <motion.div
                                initial={{ height: 0 }}
                                animate={{ height: "auto" }}
                                exit={{ height: 0 }}
                                className="overflow-hidden bg-white"
                              >
                                <div className="pt-2 px-4 grid grid-cols-1 sm:grid-cols-12 gap-3 border-t border-orange/5">
                                  {/* Member Name */}
                                  <div className="sm:col-span-5 relative">
                                    <input
                                      type="text"
                                      id={`member_name_${member.id}`}
                                      value={member.fullName}
                                      onChange={(e) => handleFamilyMemberChange(member.id, "fullName", e.target.value)}
                                      placeholder=" "
                                      className={`peer w-full bg-[#FAF9F6] text-gray-800 placeholder-transparent border rounded-xl px-4 py-2.5 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 text-sm ${
                                        errors[`member_${member.id}_name`] 
                                          ? "border-red focus:border-red" 
                                          : "border-gray-200 focus:border-orange"
                                      }`}
                                    />
                                    <label 
                                      htmlFor={`member_name_${member.id}`}
                                      className="absolute left-4 -top-2.5 bg-white px-1.5 text-xs font-semibold text-orange transition-all duration-300 pointer-events-none 
                                      peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-2.5 
                                      peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-orange"
                                    >
                                      Full Name *
                                    </label>
                                    {errors[`member_${member.id}_name`] && (
                                      <p className="text-red text-xs mt-1.5 flex items-center gap-1">
                                        <Info className="w-3 h-3" />
                                        {errors[`member_${member.id}_name`]}
                                      </p>
                                    )}
                                  </div>

                                  {/* Member Relation */}
                                  <div className="sm:col-span-3 relative">
                                    <select
                                      id={`member_relation_${member.id}`}
                                      value={member.relation}
                                      onChange={(e) => handleFamilyMemberChange(member.id, "relation", e.target.value)}
                                      className={`peer w-full bg-[#FAF9F6] text-gray-800 border rounded-xl px-4 py-2.5 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 text-sm appearance-none ${
                                        errors[`member_${member.id}_relation`] 
                                          ? "border-red focus:border-red" 
                                          : "border-gray-200 focus:border-orange"
                                      }`}
                                    >
                                      <option value="" hidden>Relation *</option>
                                      <option value="Spouse">Spouse</option>
                                      <option value="Son">Son</option>
                                      <option value="Daughter">Daughter</option>
                                      <option value="Father">Father</option>
                                      <option value="Mother">Mother</option>
                                      <option value="Brother">Brother</option>
                                      <option value="Sister">Sister</option>
                                      <option value="Grandfather">Grandfather</option>
                                      <option value="Grandmother">Grandmother</option>
                                      <option value="Other">Other</option>
                                    </select>
                                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-orange">
                                      <ChevronDown className="w-4 h-4" />
                                    </div>
                                    {errors[`member_${member.id}_relation`] && (
                                      <p className="text-red text-xs mt-1.5 flex items-center gap-1">
                                        <Info className="w-3 h-3" />
                                        {errors[`member_${member.id}_relation`]}
                                      </p>
                                    )}
                                  </div>

                                  {/* Member Phone */}
                                  <div className="sm:col-span-4 relative">
                                    <input
                                      type="tel"
                                      id={`member_phone_${member.id}`}
                                      value={member.mobileNumber}
                                      onChange={(e) => handleFamilyMemberChange(member.id, "mobileNumber", e.target.value)}
                                      placeholder=" "
                                      className={`peer w-full bg-[#FAF9F6] text-gray-800 placeholder-transparent border rounded-xl px-4 py-2.5 outline-none transition-all duration-300 focus:ring-2 focus:ring-orange/20 text-sm ${
                                        errors[`member_${member.id}_phone`] 
                                          ? "border-red focus:border-red" 
                                          : "border-gray-200 focus:border-orange"
                                      }`}
                                    />
                                    <label 
                                      htmlFor={`member_phone_${member.id}`}
                                      className="absolute left-4 -top-2.5 bg-white px-1.5 text-xs font-semibold text-orange transition-all duration-300 pointer-events-none 
                                      peer-placeholder-shown:text-sm peer-placeholder-shown:text-gray-400 peer-placeholder-shown:top-2.5 
                                      peer-focus:-top-2.5 peer-focus:text-xs peer-focus:text-orange"
                                    >
                                      Mobile (Optional)
                                    </label>
                                    {errors[`member_${member.id}_phone`] && (
                                      <p className="text-red text-xs mt-1.5 flex items-center gap-1">
                                        <Info className="w-3 h-3" />
                                        {errors[`member_${member.id}_phone`]}
                                      </p>
                                    )}
                                  </div>
                                </div>
                              </motion.div>
                            )}
                          </AnimatePresence>
                        </motion.div>
                      ))}
                    </AnimatePresence>
                  </div>
                )}

                {/* Add button */}
                <button
                  type="button"
                  onClick={handleAddFamilyMember}
                  disabled={familyMembers.length >= 5}
                  className="w-full mt-5 flex items-center justify-center gap-2 py-3 border-2 border-dashed border-orange/40 hover:border-orange text-orange hover:bg-orange/5 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl font-bold transition-all duration-300 text-sm"
                >
                  <Plus className="w-4 h-4" />
                  Add Family Member ({familyMembers.length} / 5)
                </button>
              </div>

              {/* Form Submission CTA Container */}
              <div className="bg-gradient-to-r from-red/5 to-orange/5 border border-orange/20 rounded-3xl p-6 md:p-8 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div className="space-y-1 text-center sm:text-left">
                  <h3 className="font-bold text-xl text-darkred">Register Blessings Now</h3>
                  <p className="text-sm text-gray-500">Submit details to record names for the divine puja sevas.</p>
                  <p className="text-lg font-bold text-orange italic">Jai Jagannath!</p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full sm:w-auto bg-gradient-to-r from-red to-orange hover:from-darkred hover:to-orange text-white font-bold px-10 py-4 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 disabled:opacity-65 disabled:cursor-not-allowed flex items-center justify-center gap-2.5 text-lg"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-8 h-8 animate-spin" />
                      <span>Registering...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-8 h-8" />
                      <span>Submit Blessings Request</span>
                    </>
                  )}
                </button>
              </div>

            </form>
          </div>

          {/* Right Column: Why Register & Branding */}
          <div className="lg:col-span-5 space-y-8">
            
            {/* Blessings Card Header banner */}
            <div className="bg-gradient-to-br from-[#FAF6F0] to-[#FFF] rounded-3xl border border-amber-500/10 shadow-lg p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-orange/10 to-transparent rounded-bl-full pointer-events-none" />
              <h2 className="text-xl font-bold text-darkred mb-4 relative z-10 border-b border-orange/20 pb-3 flex items-center gap-2">
                <Compass className="w-5 h-5 text-orange animate-spin-[30s]" />
                Why Register for Blessings?
              </h2>

              <div className="space-y-5">
                {/* Benefit 1 */}
                <div className="flex gap-4 items-start group">
                  <div className="p-2 bg-orange/10 text-orange rounded-xl mt-0.5 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <Sparkles className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="text-gray-800">Lord Jagannath’s Divine Mercy</h3>
                    <p className="text-sm text-gray-500 leading-relaxed mt-0.5">
                      Receive the divine blessings of Lord Jagannath, Baladeva, and Subhadra Devi directly registered under your names during the holy Rath Yatra.
                    </p>
                  </div>
                </div>

                {/* Benefit 2 */}
                <div className="flex gap-4 items-start group">
                  <div className="p-2 bg-orange/10 text-orange rounded-xl mt-0.5 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <Heart className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className=" text-gray-800">Spiritual Connection</h3>
                    <p className="text-sm text-gray-500 leading-relaxed mt-0.5">
                      Stay spiritually linked with the celebrations and feel the holy vibration of the chariot festival in your home.
                    </p>
                  </div>
                </div>

                {/* Benefit 3 */}
                <div className="flex gap-4 items-start group">
                  <div className="p-2 bg-orange/10 text-orange rounded-xl mt-0.5 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <Award className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className=" text-gray-800">Grand Legacy Celebration</h3>
                    <p className="text-sm text-gray-500 leading-relaxed mt-0.5">
                      Be a part of South India's largest and most magnificent Jagannath Rath Yatra organized by ISKCON Vijayawada.
                    </p>
                  </div>
                </div>

                {/* Benefit 4 */}
                <div className="flex gap-4 items-start group">
                  <div className="p-2 bg-orange/10 text-orange rounded-xl mt-0.5 group-hover:scale-110 transition-transform duration-300 flex-shrink-0">
                    <Calendar className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className=" text-gray-800">Updates & Event Alerts</h3>
                    <p className="text-sm text-gray-500 leading-relaxed mt-0.5">
                      Receive direct, customized updates about festival schedules, special kirtans, prasadam distribution, and temple activities.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Sacred Mantra Visual Card */}
            <div className="relative rounded-3xl overflow-hidden bg-gradient-to-br from-rose-950 to-darkred text-white p-6 sm:p-8 shadow-xl border border-rose-800">
              {/* Background Glow */}
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,rgba(222,125,13,0.15)_0%,transparent_60%)] pointer-events-none" />
              
              <div className="relative z-10 text-center space-y-4">
                <svg className="w-8 h-8 text-yellow-400 mx-auto opacity-80" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                </svg>
                <h3 className="font-dm-serif text-2xl text-yellow-300 font-bold italic">"Jagannathah Swami Nayana Patha Gami Bhavatu Me"</h3>
                <p className="text-sm text-rose-100/90 leading-relaxed max-w-sm mx-auto">
                  "O Lord of the Universe, please become the object of my vision." <br />
                  Sponsor Annadanam or register for blessings to receive the mercy of Lord Jagannath on this auspicious occasion.
                </p>
                <div className="pt-2">
                  <Link
                    to="/rathyatra"
                    className="inline-flex items-center gap-1.5 text-sm text-yellow-400 hover:text-white transition-colors duration-300 font-semibold no-underline"
                  >
                    <span>Sponsor Annadanam Prasadam</span>
                    <span>→</span>
                  </Link>
                </div>
              </div>
            </div>

          </div>

        </div>
      </div>

      <Footer />

      {/* Success Modal Overlay */}
      <AnimatePresence>
        {showSuccessModal && (
          <div className="fixed inset-0 z-[100] bg-black/65 backdrop-blur-sm flex items-center justify-center p-4">
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 350 }}
              className="bg-white rounded-3xl w-full max-w-xl max-h-[90vh] overflow-y-auto relative shadow-2xl border border-amber-500/20"
            >
              {/* Premium Top Strip */}
              <div className="h-2 bg-gradient-to-r from-red via-orange to-yellow-500 w-full" />
              
              <div className="p-8 text-center space-y-6">
                
                {/* Golden Animated Mandala Circle */}
                <div className="w-10 h-10 bg-orange/10 text-orange rounded-full flex items-center justify-center mx-auto shadow-inner relative">
                  <Sparkles className="w-5 h-5 text-orange" />
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 8, ease: "linear" }}
                    className="absolute inset-0 border border-dashed border-orange rounded-full"
                  />
                </div>

                <div className="space-y-2">
                  <h2 className="font-dm-serif text-3xl font-black text-darkred">Blessings Registered!</h2>
                  <p className="text-sm text-gray-500">Jai Jagannath! Your names have been successfully logged for the puja blessings.</p>
                </div>

                {/* Blessings details container */}
                <div className="bg-[#FAF9F6] border border-orange/10 rounded-2xl px-4 py-2 text-left space-y-4">
                  <div className="border-b border-orange/10 pb-2.5 flex justify-between items-center text-sm font-bold text-darkred">
                    <span>Devotee Details</span>
                    <span className="text-xs text-orange bg-orange/5 px-2 py-0.5 rounded-md font-semibold">Gotram: {form.gotram || "N/A"}</span>
                  </div>
                  
                  <div className="space-y-1.5 text-xs text-gray-700">
                    <p><strong className="text-gray-900">Name:</strong> {form.fullName}</p>
                    <p><strong className="text-gray-900">Mobile:</strong> +91 {form.mobileNumber}</p>
                    <p><strong className="text-gray-900">Address:</strong> {form.address}</p>
                  </div>

                  {familyMembers.length > 0 && (
                    <div className="border-t border-orange/10 pt-3 space-y-2">
                      <p className="text-sm font-bold text-darkred">Family Members Registered:</p>
                      <ul className="space-y-1 pl-0 m-0">
                        {familyMembers.map((m, idx) => (
                          <li key={m.id} className="text-xs text-gray-600 flex items-center gap-1.5">
                            <span className="w-1.5 h-1.5 rounded-full bg-orange flex-shrink-0" />
                            <span>{m.fullName} <span className="text-gray-400">({m.relation})</span></span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <p className="text-xs text-gray-400 leading-relaxed max-w-sm mx-auto">
                  A verification confirmation and event schedule updates will be sent to your mobile number. May Lord Jagannath bless you and your family.
                </p>

                <button
                  type="button"
                  onClick={handleModalClose}
                  className="w-full bg-gradient-to-r from-red to-orange hover:from-darkred hover:to-orange text-white font-extrabold py-3.5 rounded-2xl shadow-md hover:shadow-lg transition-all duration-300 text-sm uppercase tracking-wide"
                >
                  Jai Jagannath!
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
    </>
  );
};

export default RathyatraRegister;
